import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Heart, Activity, Briefcase, Sparkles, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { getGuidanceList } from "@/lib/api";

const ICONS = { Heart, Activity, Briefcase, Sparkles };

const Guidance = () => {
  const { data: guidance = [] } = useQuery({ queryKey: ["guidance"], queryFn: getGuidanceList });
  return (
    <Layout>
      <Seo
        title="Which Pooja is Right for Me? | Honest Guidance from Gokarna Priests"
        description="Confused about which pooja to do? Get honest, problem-based guidance for marriage, health, career, and dosha remedies from authentic Gokarna temple priests."
      />
      <section className="bg-secondary text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gold">Devotee Guidance</span>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl text-white tracking-tight">Which Pooja is Right for Me?</h1>
          <p className="mt-4 max-w-2xl text-white/80">Tell us your problem and we'll honestly guide you to the right ritual. We verify your situation first — and never push poojas you don't need.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {guidance.map((g, i) => {
            const Icon = ICONS[g.icon] || Sparkles;
            return (
              <Link key={g.id} to={`/guidance/${g.slug}`} data-testid={`guidance-${g.slug}`} className="reveal group bg-white border border-border rounded-xl p-8 hover:-translate-y-1 hover:shadow-lg transition-all" style={{ animationDelay: `${i * 0.07}s` }}>
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary"><Icon className="h-7 w-7" /></span>
                <h2 className="mt-5 font-heading text-2xl text-secondary">{g.title}</h2>
                <p className="mt-2 text-muted-foreground leading-relaxed">{g.problem_description}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 font-medium text-primary">See recommended poojas <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-cream border-t border-border">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 lg:px-12 py-14">
          <h2 className="font-heading text-3xl text-secondary">How We Help You Choose the Right Pooja</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">Choosing the right pooja can feel confusing — especially when you are facing a difficult phase in marriage, health, career or finances. At Shrigokarnam, our Gokarna temple priests believe guidance should come before any booking. Instead of prescribing expensive rituals for every problem, we first listen to your situation, look at the traditional shastra-based remedies, and recommend only what is genuinely helpful.</p>
          <p className="mt-4 text-muted-foreground leading-relaxed">The categories above map common life problems to the poojas most often associated with resolving them according to Vedic tradition. Marriage delays and relationship obstacles are frequently linked to Mangal (Mars) or Guru doshas; persistent health issues may call for Maha Mrityunjaya Homa; career and business stagnation is often addressed with Navagraha Shanti or Ganapati Homa; and recurring misfortune can point to Rahu-Ketu, Sarpa or Pitru dosha needing nivarana.</p>
          <p className="mt-4 text-muted-foreground leading-relaxed">Explore the guidance categories, or simply message us with your specific concern — we will guide you honestly, explain why a particular ritual is suggested, and help you perform it at the most auspicious time at the sacred Gokarna Mahabaleshwar Temple.</p>
        </div>
      </section>
    </Layout>
  );
};

export default Guidance;
